/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Tooltip } from '@crystaldelta/loree-ui-components';
import { AlignmentButton } from './editorExtensions/alignmentButton';
import {
  createSelectionState,
  splitValueAndUnit,
  getNodeAttributes,
  getFontName,
  getFontSize,
  getTextAlign,
  getLineHeight,
  getBackgroundColor,
  getTextColor,
  setTextColor,
  setBackgroundColor,
  getCurrentLink,
  isLinkText,
  setBorderSize,
  setBorderStyle,
  setBorderColor,
  getBorderColor,
  getBorderSize,
  getBorderStyle,
  applyLink,
  isBulletList,
  isOrderedList,
  setEditorConfig,
  setDefaultFontStyle,
} from './editorExtensions/utilityFunctions';
import { ReactComponent as BlockquoteButton } from './editorFormattingIcons/blockquoteButton.svg';
import { ReactComponent as LinkButton } from './editorFormattingIcons/linkButton.svg';
import { ReactComponent as ClearEditorButton } from './editorFormattingIcons/clearFormatButton.svg';
import { BackgroundColorButton } from './editorMenu/backgroundColorButton';
import { BorderColorButton } from './editorMenu/borderColorButton';
import { TextColorButton } from './editorMenu/foregroundColorButton';
import { CONSTANTS } from './editorUtilityFunctions/constants';
import { FontSectionContent } from './editorUtilityFunctions/fontstSection';
import { HeadingFormatButton } from './editorUtilityFunctions/formatButtonsHeading';
import { HistoryFormatButtons } from './editorUtilityFunctions/formatButtonHistory';
import { TextFormatButton } from './editorUtilityFunctions/formatButtonsTextFormat';
import { LineHeightButton } from './editorUtilityFunctions/lineHeightButton';
import {
  LintEditorFormatPropsType,
  LoreeInteractiveEditorDashboardContentType,
} from './editorUtilityFunctions/lintEditorType';
import { ListElementOption } from './editorUtilityFunctions/listElementOptions';
import lintStyle from './editorUtilityFunctions/loreeInteractiveEditor.module.scss';
import MediaContent from './editorUtilityFunctions/mediaContent';
import { EditorFormatButton, FormatSection } from './editorUtilityFunctions/styledContents';
import { TableFormatContent } from './editorUtilityFunctions/tableButtonElement';
import { TextIndentButton } from './editorUtilityFunctions/textIndentButton';
import { WordOptionsButton } from './editorUtilityFunctions/wordOptionButton';
import { findNodeTypeFromSelection } from './editorExtensions/findNodes';
import { IconDiv } from './editorUtilityFunctions/utilityFunctions';
import { ReactComponent as EmbedButton } from './editorFormattingIcons/embedButton.svg';
import EmbedImageModal from './editorUtilityFunctions/embedContentModal';
import { ReactComponent as EnterFullScreen } from '../../assets/icons/enterFullScreen.svg';
import { ReactComponent as ExitFullScreen } from '../../assets/icons/exitFullScreen.svg';
import LinksModal from '../formComponent/links/linksModal';
import { Editor } from '@tiptap/core';
import { Level } from '@tiptap/extension-heading';
import { clearContentFormatInsideEditor } from './editorExtensions/clearContentFormat';

export function LoreeInteractiveEditorFormatOptions(props: LintEditorFormatPropsType) {
  const { editor, editorConfig, config } = props;
  if (!editor) {
    return null;
  }
  if (editorConfig) {
    setEditorConfig(editorConfig);
  }

  const onChangeLineHeight = (value: string) => {
    editor.commands.setLineHeight(value);
  };
  const [selectionTypeName, setSelectionTypeName] = useState('');
  const [fontFamilyName, setFontFamilyName] = useState(getFontName(editor).trim() ?? 'Lato');
  const [fontSizeValue, setFontSizeValue] = useState({
    size: 16,
    unit: 'px',
  });
  const [textAlign, setTextAlign] = useState('');
  const [lineHeight, setLineHeight] = useState<string | null>('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkAttrs, setLinkAttrs] = useState({ url: '', newWindow: true });
  const [isUploading, setIsUploading] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [enableIcon, setEnableIcon] = useState(false);
  const [isItalicButtonActive, setIsItalicButtonActive] = useState(false);
  const isDrogAndDrop = props.type === 'DragandDrop';
  useEffect(() => {
    const { selection } = editor.state;
    const selectionState = createSelectionState(selection);
    setSelectionTypeName(selectionState?.selectedNode?.node.type.name ?? '');
    if (editor) {
      setFontFamilyName(getFontName(editor) ?? 'Source sans pro');
      setFontSizeValue(getFontSize(editor) ?? fontSizeValue);
      setTextAlign(getTextAlign(editor));
      setLineHeight(getLineHeight(editor));
      setLinkAttrs(getCurrentLink(editor));
    }
  }, [editor.state.selection]);

  const getTextIndent = (typeName: string) => {
    let value = '0';
    const attr = getNodeAttributes(editor.state, typeName);
    const res = splitValueAndUnit(attr['textIndent'] as string);
    if (res?.value) value = res.value;
    return value;
  };

  const setTextIndent = (value: string | null) => {
    editor.commands.setTextIndent(`${value}em`);
  };

  const getIndentValue = (indentType: string, typeName: string): string => {
    if (indentType === 'textIndent') {
      return getTextIndent(typeName);
    }
    return getLeftPadding(typeName);
  };

  const setIndentValue = (indentType: string, indentValue: string): void => {
    if (indentType === 'textIndent') {
      setTextIndent(indentValue);
    } else {
      const { selection } = editor.state;
      const isTableSelected = findNodeTypeFromSelection('table', selection).node;
      if (isTableSelected && indentValue === '0') {
        editor.commands.setLeftPadding('5px');
        return;
      }
      setLeftPadding(indentValue);
    }
  };

  const setIndentSpacing = (indentType: string, indentOption: string) => {
    const currentIndentValue = getIndentValue(indentType, selectionTypeName);
    let updatedIndentValue: number;
    switch (indentOption) {
      case 'increase': {
        updatedIndentValue = Number(currentIndentValue) + CONSTANTS.LOREE_TEXT_INDENT_INDEX;
        setIndentValue(indentType, updatedIndentValue.toString());
        break;
      }
      case 'decrease':
        if (Number(currentIndentValue) < CONSTANTS.LOREE_TEXT_INDENT_INDEX) {
          setIndentValue(indentType, '0');
        } else {
          updatedIndentValue = Number(currentIndentValue) - CONSTANTS.LOREE_TEXT_INDENT_INDEX;
          setIndentValue(indentType, updatedIndentValue.toString());
        }
        break;
    }
  };

  const setLeftPadding = (value: string | null) => {
    editor.commands.setLeftPadding(`${value}em`);
  };

  const getLeftPadding = (typeName: string) => {
    let value = '0';
    const attr = getNodeAttributes(editor.state, typeName);
    const res = splitValueAndUnit(attr['paddingLeft'] as string);
    if (attr['paddingLeft'] === '5px') return value;
    if (res?.value) {
      value = res.value;
    }
    return value;
  };

  const handleApplyLink = (url: string, isNewTab: boolean) => {
    setIsItalicButtonActive(false);
    setShowLinkModal(false);
    let adminDashboardLinkStyles = getAdminDashboardLinkStyles(editorConfig);
    adminDashboardLinkStyles = adminDashboardLinkStyles.replace(
      /(font-family\s*:\s*[^;]+?)-(italic|normal)(?=;)/i,
      '$1'
    );
    applyLink(editor, url, isNewTab, adminDashboardLinkStyles);
  };

  const getAdminDashboardLinkStyles = (
    editorConfig: LoreeInteractiveEditorDashboardContentType | undefined,
  ) => {
    const linkStyles = editorConfig?.customLinkStyleList[0];
    let styles;
    if (linkStyles) {
      styles = `font-family: ${linkStyles['font']}; color: ${linkStyles['color']
        }; text-decoration: ${linkStyles['text-decoration']}; text-decoration-style: ${linkStyles['text-decoration-style']
        }; ${linkStyles['text-style'] === 'Bold'
          ? `font-weight: bold;`
          : `font-style: ${linkStyles['text-style']};`
      }`;
    }
    return styles ?? '';
  };
  return (
    <FormatSection>
      {!isDrogAndDrop && (
        <>
          <HistoryFormatButtons editor={editor} />
          <HeadingFormatButton
            editorConfig={editorConfig}
            editor={editor}
            setEnableIcon={setEnableIcon}
            setIsItalicButtonActive={setIsItalicButtonActive}
          />
          <TextFormatButton
            editor={editor}
            enableIcon={enableIcon}
            setIsItalicButtonActive={setIsItalicButtonActive}
          />
        </>
      )}
      <FontSectionContent
        editor={editor}
        editorConfig={editorConfig}
        fontFamilyName={fontFamilyName}
        fontSizeValue={fontSizeValue}
        setEnableIcon={setEnableIcon}
        fontStyleValue={props.fontStyleValue}
        enableIcon={enableIcon}
        setIsItalicButtonActive={setIsItalicButtonActive}
        isItalicButtonActive={isItalicButtonActive}
        isDrogAndDrop={isDrogAndDrop}
      />
      <TextColorButton
        onChange={(color) => setTextColor(color, editor)}
        currentColor={getTextColor(editor)}
        globalColorList={[]}
        sharedColors={[]}
        colorPickerFeatures={CONSTANTS.LOREE_COLORPICKER_FEATURES}
        colorContrastRatio={() => { }}
        colorPickerType='text'
      ></TextColorButton>
      {!isDrogAndDrop && (
        <>
          <BackgroundColorButton
            onChange={(color) => setBackgroundColor(color, editor)}
            currentColor={getBackgroundColor(editor)}
            globalColorList={[]}
            sharedColors={[]}
            colorPickerFeatures={CONSTANTS.LOREE_COLORPICKER_FEATURES}
            colorContrastRatio={() => {}}
            colorPickerType='Background'
          />
          <BorderColorButton
            onChange={(color) => setBorderColor(editor, color)}
            currentColor={getBorderColor(editor)}
            globalColorList={[]}
            sharedColors={[]}
            colorPickerFeatures={CONSTANTS.LOREE_COLORPICKER_FEATURES}
            colorContrastRatio={() => {}}
            colorPickerType='Background'
            currentSize={getBorderSize(editor)}
            currentStyle={getBorderStyle(editor)}
            onChangeSize={(size) => setBorderSize(editor, size)}
            onChangeStyle={(style) => setBorderStyle(editor, style)}
          />
          <ListElementOption editor={editor} />
          <AlignmentButton
            isMedia={
              editor.isActive('iframe') || editor.isActive('image') || editor.isActive('iconTag')
            }
            activeLeft={textAlign === 'left'}
            activeCenter={textAlign === 'center'}
            activeRight={textAlign === 'right'}
            activeJustify={textAlign === 'justify'}
            onLeft={() => {
              if (editor.isActive('iconTag')) {
                editor.commands.setAlignmentIconTag('left');
              } else editor.commands.setAlignment('left');
            }}
            onCenter={() => {
              if (editor.isActive('iconTag')) {
                editor.commands.setAlignmentIconTag('center');
              } else editor.commands.setAlignment('center');
            }}
            onRight={() => {
              if (editor.isActive('iconTag')) {
                editor.commands.setAlignmentIconTag('right');
              } else editor.commands.setAlignment('right');
            }}
            onJustify={() => editor.commands.setAlignment('justify')}
          />
          <TextIndentButton
            {...{
              setIndentSpacing,
              getLeftPadding,
              getTextIndent,
              selectionTypeName,
              editor,
            }}
          />
          <EditorFormatButton
            className={
              editor.isActive('blockquote')
                ? `${lintStyle.formatButton} is-active`
                : `${lintStyle.textFormatButton}`
            }
            onClick={() => editor.commands.toggleBlockquoteContent()}
            disabled={isBulletList(editor) || isOrderedList(editor)}
          >
            <Tooltip info='Blockquote'>
              <IconDiv enableIcon={isBulletList(editor) || isOrderedList(editor)}>
                <BlockquoteButton className='textFormatButton' />
              </IconDiv>
            </Tooltip>
          </EditorFormatButton>
          <WordOptionsButton editor={editor} />
          <LineHeightButton {...{ lineHeight, onChangeLineHeight }} />
          <MediaContent
            editor={editor}
            setIsUploading={(value: boolean) => setIsUploading(value)}
          />
          <TableFormatContent editor={editor} editorConfig={editorConfig} />
          <EditorFormatButton
            className={
              isLinkText(editor)
                ? `${lintStyle.formatButton} is-active`
                : `${lintStyle.textFormatButton}`
            }
            onClick={() => setShowLinkModal(true)}
          >
            <Tooltip info='Add Link'>
              <IconDiv>
                <LinkButton className='textFormatButton' />
              </IconDiv>
            </Tooltip>
          </EditorFormatButton>
          <LinksModal
            showModal={showLinkModal}
            onHide={() => setShowLinkModal(false)}
            type='link'
            url={linkAttrs.url}
            handleApplyFromLink={handleApplyLink}
          />

          <EditorFormatButton onClick={() => clearContentFormatInsideEditor(editor, editorConfig)}>
            <Tooltip info='Clear format'>
              <IconDiv>
                <ClearEditorButton className={lintStyle.formatButton} />
              </IconDiv>
            </Tooltip>
          </EditorFormatButton>
          <EditorFormatButton
            onClick={() => setShowEmbedModal(true)}
            disabled={isBulletList(editor) || isOrderedList(editor)}
          >
            <Tooltip info='Embed'>
              <IconDiv enableIcon={isBulletList(editor) || isOrderedList(editor)}>
                <EmbedButton className='EmbedButton' />
              </IconDiv>
            </Tooltip>
          </EditorFormatButton>
          <EditorFormatButton
            className={config?.isFullScreen ? 'active' : ''}
            onClick={() => config?.setIsFullScreen((prev: boolean) => !prev)}
          >
            <Tooltip
              info={config?.isFullScreen ? 'Exit full screen' : 'Full screen'}
              key={config?.isFullScreen ? 'exit' : 'enter'}
            >
              <IconDiv>{config?.isFullScreen ? <ExitFullScreen /> : <EnterFullScreen />}</IconDiv>
            </Tooltip>
          </EditorFormatButton>
          {isUploading && (
            <small className='ms-1 text-primary'>
              <i className='fas fa-sync fa-spin' /> Uploading image
            </small>
          )}
          <EmbedImageModal
            key={showEmbedModal ? 'open' : 'closed'}
            show={showEmbedModal}
            setShowEmbedModal={setShowEmbedModal}
            editor={editor}
          />
        </>
      )}
    </FormatSection>
  );
}
